import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { ExperimentOutlined, LoadingOutlined, ProjectOutlined } from '@ant-design/icons';

import {
  DetailsHeaderActionGroup,
  DetailsHeaderActions,
  DetailsHeaderButton,
  DetailsHeaderEditActions,
} from '../../components/DetailsHeaderActions';
import { FlexibleDetailsHeaderLayout } from '../../components/FlexibleDetailsHeaderLayout';

import { AnonymiseButton } from './AnonymiseButton';
import { CV_READING_CUTOFF_MESSAGE } from './cvSuggestionsStatus';
import { SaveNewCandidateButton } from './SaveNewCandidateButton';
import { useOpenCvSuggestionsPage } from './useOpenCvSuggestionsPage';

const AiStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 4px;
  background: #eef3f8;
  color: #536779;
  font-size: 11px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;

type DetailsPageHeaderProps = {
  title?: string | ReactNode;
  candidateId?: string;
  disabled?: boolean;
  aiSuggestionsPendingCount?: number;
  isAiSuggestionsProcessing?: boolean;
  isAiSuggestionsConsentBlocked?: boolean;
  isAiSuggestionsCutoffBlocked?: boolean;
  hasUnsavedEdits?: boolean;
  onShowRecommendations?: () => void;
  onSave: () => void;
  onCancel: () => void;
};

export const CandidateDetailsPageHeader: React.FC<DetailsPageHeaderProps> = ({
  title,
  candidateId,
  disabled,
  aiSuggestionsPendingCount = 0,
  isAiSuggestionsProcessing = false,
  isAiSuggestionsConsentBlocked = false,
  isAiSuggestionsCutoffBlocked = false,
  hasUnsavedEdits = false,
  onShowRecommendations,
  onCancel,
  onSave,
}) => {
  const openCvSuggestions = useOpenCvSuggestionsPage(candidateId ?? '');

  return (
    <FlexibleDetailsHeaderLayout
      title={title}
      rootTestId="candidate-details-header"
      titleTestId="candidate-details-header-title"
      actionsTestId="candidate-details-header-actions"
      actions={
        <DetailsHeaderActions>
          <DetailsHeaderActionGroup>
            {candidateId && onShowRecommendations && (
              <DetailsHeaderButton icon={<ProjectOutlined aria-hidden />} onClick={onShowRecommendations}>
                Project matches
              </DetailsHeaderButton>
            )}
            {candidateId && (
              <DetailsHeaderButton
                icon={<ExperimentOutlined aria-hidden />}
                onClick={() => openCvSuggestions(hasUnsavedEdits)}
                title={
                  isAiSuggestionsConsentBlocked
                    ? 'Data Processing and Offers consent is required before the CV can be read'
                    : isAiSuggestionsCutoffBlocked
                    ? CV_READING_CUTOFF_MESSAGE
                    : isAiSuggestionsProcessing
                    ? 'Processing CV…'
                    : 'Review values read from the CV'
                }
              >
                <span>CV suggestions</span>
                {isAiSuggestionsConsentBlocked ? (
                  <AiStatus aria-label="Consent required to read the CV">Consent</AiStatus>
                ) : isAiSuggestionsCutoffBlocked ? (
                  <AiStatus aria-label="Reading CVs unavailable">Unavailable</AiStatus>
                ) : isAiSuggestionsProcessing ? (
                  <AiStatus aria-label="Processing CV">
                    <LoadingOutlined spin aria-hidden />
                    Processing
                  </AiStatus>
                ) : aiSuggestionsPendingCount > 0 ? (
                  <AiStatus aria-label={`${aiSuggestionsPendingCount} pending suggestions`}>
                    {aiSuggestionsPendingCount}
                  </AiStatus>
                ) : null}
              </DetailsHeaderButton>
            )}
            {candidateId && <AnonymiseButton candidateId={candidateId} />}
          </DetailsHeaderActionGroup>
          <DetailsHeaderEditActions>
            <DetailsHeaderButton onClick={onCancel}>Cancel</DetailsHeaderButton>
            <SaveNewCandidateButton disabled={disabled} onSave={onSave} />
          </DetailsHeaderEditActions>
        </DetailsHeaderActions>
      }
    />
  );
};
